import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { Pagination } from '../../../components/ui/Pagination'
import { Alert } from '../../../components/ui/Alert'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { FormField, inputClass, selectClass } from '../../../components/ui/FormField'
import { DateInput, toBrDate, toISODate } from '../../../components/ui/DateInput'
import { useAtiradores, useCreateAtirador, useUpdateAtirador, useDeleteAtirador } from '../hooks/useAtiradores'
import { useClubes } from '../../clubes/hooks/useClubes'
import { atiradorFormSchema, type AtiradorFormData } from '../schemas'
import { useDebounce } from '../../../hooks/useDebounce'
import type { Atirador } from '../types'

export function AtiradorPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({ nome: '', cpf: '', numero_cr: '' })
  const debouncedFilters = useDebounce(filters, 400)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Atirador | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Atirador | null>(null)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [showArmasDialog, setShowArmasDialog] = useState(false)
  const [newAtiradorId, setNewAtiradorId] = useState<number | null>(null)

  const { data, isLoading } = useAtiradores({ ...debouncedFilters, page, limit: 20 })
  const { data: clubesData } = useClubes({ limit: 200 })
  const createAtirador = useCreateAtirador()
  const updateAtirador = useUpdateAtirador()
  const deleteAtirador = useDeleteAtirador()

  const form = useForm<AtiradorFormData>({
    resolver: zodResolver(atiradorFormSchema),
    defaultValues: { idclube: 0, nome: '', cpf: '', data_nascimento: '', email: '', telefone: '', numero_cr: '' },
  })

  function openCreate() {
    setEditing(null)
    form.reset({ idclube: 0, nome: '', cpf: '', data_nascimento: '', email: '', telefone: '', numero_cr: '' })
    setModalOpen(true)
  }

  function openEdit(a: Atirador) {
    setEditing(a)
    form.reset({
      idclube: Number(a.idclube),
      nome: a.nome,
      cpf: a.cpf,
      data_nascimento: a.data_nascimento ? toBrDate(a.data_nascimento) : '',
      email: a.email ?? '',
      telefone: a.telefone ?? '',
      numero_cr: a.numero_cr ?? '',
    })
    setModalOpen(true)
  }

  async function handleSubmit(rawData: AtiradorFormData) {
    const formData = {
      ...rawData,
      data_nascimento: toISODate(rawData.data_nascimento) || rawData.data_nascimento,
    }
    try {
      if (editing) {
        await updateAtirador.mutateAsync({ id: editing.idatirador, data: formData })
        setAlert({ type: 'success', message: 'Atirador atualizado com sucesso' })
        setModalOpen(false)
      } else {
        const created = await createAtirador.mutateAsync(formData as any)
        setNewAtiradorId((created as any).idatirador)
        setModalOpen(false)
        setShowArmasDialog(true)
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao salvar atirador' })
    }
  }

  function handleArmasYes() {
    setShowArmasDialog(false)
    setAlert({ type: 'success', message: 'Atirador cadastrado!' })
    navigate(`/armas?idatirador=${newAtiradorId}`)
  }

  function handleArmasNo() {
    setShowArmasDialog(false)
    setAlert({ type: 'success', message: 'Atirador cadastrado com sucesso' })
  }

  async function handleDelete() {
    if (!deleteTarget) return
    try {
      await deleteAtirador.mutateAsync(deleteTarget.idatirador)
      setAlert({ type: 'success', message: 'Atirador excluido com sucesso' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao excluir atirador' })
    } finally {
      setDeleteTarget(null)
    }
  }

  const meta = data?.meta
  const isSaving = createAtirador.isPending || updateAtirador.isPending

  return (
    <div className="space-y-4">
      <PageHeader
        title="Atiradores"
        description="Gerenciar atiradores"
        action={
          <button onClick={openCreate} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro">
            + Novo Atirador
          </button>
        }
      />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Filtros */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Nome</label>
            <input className={inputClass} placeholder="Nome..." value={filters.nome}
              onChange={e => { setPage(1); setFilters(f => ({ ...f, nome: e.target.value })) }} />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">CPF</label>
            <input className={inputClass} placeholder="CPF..." value={filters.cpf}
              onChange={e => { setPage(1); setFilters(f => ({ ...f, cpf: e.target.value })) }} />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">N. CR</label>
            <input className={inputClass} placeholder="Numero CR..." value={filters.numero_cr}
              onChange={e => { setPage(1); setFilters(f => ({ ...f, numero_cr: e.target.value })) }} />
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setFilters({ nome: '', cpf: '', numero_cr: '' }); setPage(1) }}
              className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Limpar</button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !data?.data?.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">🎯</p>
            <p className="font-medium">Nenhum atirador encontrado</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Nome</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">CPF</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Clube</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">N. CR</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Email</th>
                  <th className="text-right px-4 py-3 font-medium text-compumaq-textoSuave">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-compumaq-dourado/25">
                {data.data.map((a) => (
                  <tr key={a.idatirador} className="hover:bg-compumaq-preto">
                    <td className="px-4 py-3 font-medium text-compumaq-texto">{a.nome}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave font-mono text-xs">{a.cpf}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{a.clube_nome ?? '—'}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{a.numero_cr ?? '—'}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave">{a.email ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => navigate(`/armas?idatirador=${a.idatirador}`)}
                          className="text-xs text-green-600 hover:text-green-800 font-medium">Armas</button>
                        <button onClick={() => openEdit(a)}
                          className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium">Editar</button>
                        <button onClick={() => setDeleteTarget(a)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium">Excluir</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {meta && meta.totalPages > 1 && (
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={20} onPageChange={setPage} />
            )}
          </>
        )}
      </div>

      {/* Modal form */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Atirador' : 'Novo Atirador'} size="lg">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField label="Clube" required error={form.formState.errors.idclube?.message}>
            <Controller
              control={form.control}
              name="idclube"
              render={({ field }) => (
                <select
                  className={selectClass}
                  value={field.value || ''}
                  onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}
                >
                  <option value="">Selecione um clube...</option>
                  {clubesData?.data?.map(c => (
                    <option key={c.idclube} value={c.idclube}>{c.nome} — {c.cidade}/{c.estado}</option>
                  ))}
                </select>
              )}
            />
          </FormField>
          <FormField label="Nome completo" required error={form.formState.errors.nome?.message}>
            <input className={inputClass} {...form.register('nome')} placeholder="Nome do atirador" />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="CPF" required error={form.formState.errors.cpf?.message}>
              <input className={inputClass} {...form.register('cpf')} placeholder="000.000.000-00" />
            </FormField>
            <FormField label="Data de nascimento" required error={form.formState.errors.data_nascimento?.message}>
              <Controller
                name="data_nascimento"
                control={form.control}
                render={({ field }) => (
                  <DateInput
                    value={field.value}
                    onChange={field.onChange}
                    className={inputClass}
                    required
                  />
                )}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Email" error={form.formState.errors.email?.message}>
              <input className={inputClass} {...form.register('email')} placeholder="email@exemplo.com" />
            </FormField>
            <FormField label="Telefone" error={form.formState.errors.telefone?.message}>
              <input className={inputClass} {...form.register('telefone')} placeholder="(11) 99999-9999" />
            </FormField>
          </div>
          <FormField label="Numero CR" error={form.formState.errors.numero_cr?.message}>
            <input className={inputClass} {...form.register('numero_cr')} placeholder="Numero do CR" />
          </FormField>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)}
              className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Cancelar</button>
            <button type="submit" disabled={isSaving}
              className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50">
              {isSaving ? 'Salvando...' : editing ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Dialog - cadastrar armas */}
      <Modal open={showArmasDialog} onClose={handleArmasNo} title="Atirador cadastrado!" size="sm">
        <p className="text-compumaq-textoSuave text-sm mb-6">Deseja cadastrar armas para este atirador agora?</p>
        <div className="flex justify-end gap-3">
          <button onClick={handleArmasNo}
            className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">
            Nao, voltar para listagem
          </button>
          <button onClick={handleArmasYes}
            className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro">
            Sim, cadastrar armas
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Atirador"
        message={`Tem certeza que deseja excluir o atirador "${deleteTarget?.nome}"?`}
        loading={deleteAtirador.isPending}
      />
    </div>
  )
}
